import styled from 'styled-components'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '../../utils/useClient'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getMarketLanguageState } from '../../store/appSlice'
import {
  deleteTruckFromGarageList,
  getGarageListState,
  getGuidedViewPage,
  getSessionIdState,
  loggedInStatus,
} from '../../store/sessionDataSlice'
import {
  TdsAccordion,
  TdsAccordionItem,
  TdsButton,
  TdsInlineTab,
  TdsInlineTabs,
  TdsSpinner,
  TdsTextField,
} from '@scania/tegel-react'
import { buildRelativeOverviewPage } from '../../utils/UrlBuilders'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { UserConfigInfo } from '../../api/generated'
import { SESSION_FAILURE } from '../../api/errors'
import {
  ConsequenceOfChangeDialogData,
  ConsequenceOfChangeHandler,
} from '../../types/ConsequenceOfChangeTypes'
import {
  NewTruckLoadedHandler,
  NewTruckLoadedInfo,
  TruckLoadedSource,
} from '../../types/TruckLoadedTypes'
import { setGvSidePanelContent } from '../../store/sidePanelSlice'
import { TestElementTypeId } from '../../types/TestAttributeId'
import { useNavigate } from 'react-router-dom'
import { closeAllModals } from '../../store/modalSlice'
import { getUrlParametersToString } from '../../utils/getUrlParametersToString'
import { CocOpenedFromType, SidePanelContent } from '../../store/types'

// This sidepanel has 2 different tabs:
// - Garage
// - Account
//
// The Account tab has 2 different layouts
// - My Account
// - Delete account

const GarageRoot = styled.div``

const SpinnerWrapper = styled.div`
  margin-bottom: 24px;
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: var(--tds-blue-600);
  }
`

const TabContentContainer = styled.div`
  margin: 16px;
`

const ContentContainer = styled.div`
  margin: 16px;
`

const Header = styled.h4`
  margin-top: 24px;
  margin-bottom: 16px;
`

const TextWrapper = styled.div`
  margin-bottom: 16px;
`

const TruckCard = styled.div`
  border-right: 1px solid var(--tds-grey-300);
  border-left: 1px solid var(--tds-grey-300);
  border-top: 1px solid var(--tds-grey-300);
  margin-top: 16px;
`

const TruckHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 8px;
`

const TruckImageWrapper = styled.div`
  width: 100px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 72px;
  }
`

const TruckTitleModelWrapper = styled.div`
  margin-left: 16px;
  width: 160px;
`

const TruckTitle = styled.div`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 16px;
  padding-bottom: 4px;
`

const TruckModel = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: var(--tds-grey-500);
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const TruckSpecificationWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 8px;
`

const TruckSpecificationTitles = styled.div`
  color: var(--tds-grey-700);
  font-weight: bold;
`

const TruckSpecificationText = styled.div`
  color: var(--tds-grey-700);
`

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 16px;
  width: 100%;
  gap: 8px;
`

const Divider = styled.div`
  border-bottom: 1.5px solid var(--tds-grey-300);
  margin-top: 24px;
  margin-bottom: 24px;
`

const ButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 8px;
`

const ConfirmViewText = styled.div`
  margin-top: 16px;
`

enum SidePanelGarageAccountContent {
  'garage',
  'account',
  'deleteAccount',
}

interface GarageViewProps {
  handleConsequenceOfChange: ConsequenceOfChangeHandler
  handleFatalError: () => void
  handleNewTruckIsLoaded: NewTruckLoadedHandler
  handleSessionFailure: () => void
  handleTruckHasBeenDeleted: (truck: UserConfigInfo) => void
  handleCloseModalConsequenseOfChange: () => void
}

export function ContentGarage({
  handleConsequenceOfChange,
  handleFatalError,
  handleNewTruckIsLoaded,
  handleSessionFailure,
  handleTruckHasBeenDeleted,
  handleCloseModalConsequenseOfChange,
}: GarageViewProps): JSX.Element {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const client = useClient()
  const navigate = useNavigate()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionId = useAppSelector(getSessionIdState)
  const garageList = useAppSelector(getGarageListState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)

  const [tabContent, setTabContent] = useState<SidePanelGarageAccountContent>(
    SidePanelGarageAccountContent.garage,
  )
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [filteredGarageList, setFilteredGarageList] = useState<
    UserConfigInfo[] | null
  >(garageList)
  const [truckIsLoading, setTruckIsLoading] = useState<UserConfigInfo | null>(
    null,
  )
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleLoadTruck = useCallback(
    async (item: UserConfigInfo) => {
      if (!sessionId || !client || !marketLanguage) {
        return
      }
      setTruckIsLoading(item)
      const loadConfigResult = await client.userConfigV2Load(sessionId, item.id)
      if (loadConfigResult.error || !loadConfigResult.success) {
        console.error(
          'Failed to load garage config, error: ' + loadConfigResult.error,
        )
        handleFatalError()
        return
      }

      const coc = await client.getInitialConsequenceOfChange({
        sessionId,
      })
      if (coc.error === SESSION_FAILURE) {
        console.error('getInitialConsequenceOfChange failed: ' + coc.error)
        handleSessionFailure()
        return
      } else if (coc.error) {
        console.error('getInitialConsequenceOfChange failed: ' + coc.error)
        handleFatalError()
        return
      }
      var deactivatedItems =
        coc.getInitialConsequenceOfChange?.deactivatedItems || []
      var triggerItems = coc.getInitialConsequenceOfChange?.triggerItems || []
      if (deactivatedItems.length > 0 || triggerItems.length > 0) {
        const data: ConsequenceOfChangeDialogData = {
          resolvers: {
            deactivatedItems,
            triggerItems,
            menuTexts: coc.getInitialConsequenceOfChange?.menuTexts || {},
          },

          // When loading a new configuration, we don't support rollback and
          // must display the Consequence of Change message with an OK button
          // and witout any Cancel or close buttons.
          //
          // IMPORTANT:
          // In this context, Consequence of Change is actually incorrect
          // terminology and should probably be changed to a dedicated request
          // or at least return type as a property on the userConfigV2Load
          // respone object. What this data represents when loading a new truck
          // is the difference from when it was saved, which parts of the saved
          // configuration that is no longer supported in this combination by
          // the new product rules.
          //
          // TODO: Refactor CoC for load config situations and report a
          // "LoadedConfigurationDifference" as part fo the response object or
          // something like that.
          showCancelButton: false,

          clickedId: null,
        }
        await handleConsequenceOfChange(data, CocOpenedFromType.xml)
        dispatch(closeAllModals())
        handleCloseModalConsequenseOfChange()
      }

      const relativeUrl = buildRelativeOverviewPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      setTruckIsLoading(null)
      const info: NewTruckLoadedInfo = {
        mainMenus: loadConfigResult.success.mainMenus,
        factoryModelName: coc.factoryModelName || null,
        isFactoryModel: coc.isFactoryModel || false,
        publicConfigId: null,
        savedAsId: item.id,
        savedAsName: item.name,
        source: TruckLoadedSource.USER_GARAGE,
        timeLoaded: new Date(),
      }
      handleNewTruckIsLoaded(info)
      if (!guidedViewPage) {
        navigate(newUrl)
      }
    },
    [
      client,
      dispatch,
      handleConsequenceOfChange,
      handleFatalError,
      handleNewTruckIsLoaded,
      handleSessionFailure,
      marketLanguage,
      navigate,
      sessionId,
      guidedViewPage,
      handleCloseModalConsequenseOfChange,
    ],
  )

  const handleDeleteTruck = useCallback(
    async (item: UserConfigInfo) => {
      try {
        if (!sessionId) {
          throw new Error('Expected sessionId to be defined.')
        }
        if (!client) {
          throw new Error('Expected client to be defined.')
        }
        await client.userConfigV2Delete(sessionId, item.id)
        handleTruckHasBeenDeleted(item)
      } catch (err) {
        console.error(err)
        handleFatalError()
        return
      }
      dispatch(deleteTruckFromGarageList(item.id))
    },
    [client, dispatch, handleFatalError, handleTruckHasBeenDeleted, sessionId],
  )

  const handleSearchInput = (e: any) => {
    const newSearchString = e.target.value
    setSearchFilter(newSearchString)
  }

  // Filter on search string
  useEffect(() => {
    if (!garageList) {
      return
    }
    const updatedGarageList = garageList.filter(
      (truck) =>
        truck.name.toUpperCase().includes(searchFilter.toUpperCase()) ||
        truck.description.toUpperCase().includes(searchFilter.toUpperCase()),
    )
    setFilteredGarageList(updatedGarageList)
  }, [searchFilter, garageList])

  // Initital state on changed view
  useEffect(() => {
    setSearchFilter('')
    setFilteredGarageList(garageList)
    //setTabContent('garage')
    //setErrorMessage('')
  }, [tabContent, garageList])

  const handleDeleteAccountClick = () => {
    setTabContent(SidePanelGarageAccountContent.deleteAccount)
  }

  const handleLogout = useCallback(async () => {
    if (!sessionId) {
      throw new Error('Expected sessionId to be defined.')
    }
    const response = await client?.logout(sessionId)
    if (response?.error === SESSION_FAILURE) {
      handleSessionFailure()
      return
    } else if (response?.error) {
      handleFatalError()
      return
    }
    dispatch(loggedInStatus({ isloggedin: false, userMail: '' }))
    dispatch(setGvSidePanelContent(SidePanelContent.login))
  }, [dispatch, client, sessionId, handleFatalError, handleSessionFailure])

  // TODO - display error and confirmation message
  const handleDeleteAccount = useCallback(async () => {
    if (!sessionId) {
      throw new Error('Expected sessionId to be defined.')
    }
    try {
      await client?.sendDeleteUserMail(sessionId)
    } catch (err) {
      setErrorMessage(
        'An error occurred while trying to remove your account. Try again later.',
      ) // TODO - text in sc admin
      console.error(err)
      return
    }
    handleLogout()
  }, [client, handleLogout, sessionId])

  useEffect(() => {
    setErrorMessage('')
  }, [])

  return (
    <GarageRoot>
      <TdsInlineTabs default-selected-index="0">
        <TdsInlineTab
          data-test-element-type={TestElementTypeId.GarageViewGarageTab}
          onClick={() => setTabContent(SidePanelGarageAccountContent.garage)}
        >
          <button>{t2(TextId.ACTION_LABEL_MY_GARAGE)}</button>
        </TdsInlineTab>
        <TdsInlineTab
          data-test-element-type={TestElementTypeId.GarageViewAccountTab}
          onClick={() => setTabContent(SidePanelGarageAccountContent.account)}
        >
          <button>{t2(TextId.ACTION_LABEL_MY_ACCOUNT)}</button>
        </TdsInlineTab>
      </TdsInlineTabs>

      {tabContent === SidePanelGarageAccountContent.garage && (
        <TabContentContainer
          data-test-element-type={
            TestElementTypeId.GarageViewGarageTabContainter
          }
        >
          {!garageList || garageList.length === 0 ? (
            <ContentContainer>
              <Header>{t2(TextId.HEADER_GARAGE_IS_EMPTY)}</Header>
              <TextWrapper>{t2(TextId.TEXT_GARAGE_IS_EMPTY)}</TextWrapper>
            </ContentContainer>
          ) : (
            <ContentContainer>
              <Header>Manage your garage</Header>
              <div style={{ width: '100%', marginTop: '16px' }}>
                <TdsTextField
                  style={{ height: '68px', minWidth: '230px' }}
                  type="text"
                  placeholder={t2(TextId.SEARCH)}
                  onInput={handleSearchInput}
                  value={searchFilter}
                />
              </div>
              {filteredGarageList?.map((item, i) => (
                <TruckCard
                  data-test-element-type={
                    TestElementTypeId.GarageViewGarageTabTruckCard
                  }
                  key={'truck-id-' + item.id + '-' + item.name}
                >
                  {item.id && (
                    <TruckHeaderWrapper>
                      <TruckImageWrapper>
                        <img src={item.images[1].url} alt="Truck thumbnail" />
                      </TruckImageWrapper>
                      <TruckTitleModelWrapper>
                        <TruckTitle
                          data-test-element-type={
                            TestElementTypeId.GarageViewGarageTabTruckCardTitle
                          }
                        >
                          {item.name}
                        </TruckTitle>
                        <TruckModel>
                          {item.description.toUpperCase()}
                        </TruckModel>
                      </TruckTitleModelWrapper>
                    </TruckHeaderWrapper>
                  )}
                  <TdsAccordion
                    data-test-element-type={
                      TestElementTypeId.GarageViewGarageTabTruckCardDetails
                    }
                  >
                    <TdsAccordionItem header={t2(TextId.LABEL_DETAILS)}>
                      <TruckSpecificationWrapper key={i}>
                        {item.userConfigAux ? (
                          JSON.parse(item.userConfigAux.activeSummary).flatMap(
                            (data: any, i: number) => [
                              <TruckSpecificationTitles
                                key={
                                  'truck-specification-title-' +
                                  item.id +
                                  '-' +
                                  item.name +
                                  '-' +
                                  i
                                }
                              >
                                {data.group}:&nbsp;
                              </TruckSpecificationTitles>,
                              <TruckSpecificationText
                                key={
                                  'truck-specification-text-' +
                                  item.id +
                                  '-' +
                                  item.name +
                                  '-' +
                                  i
                                }
                              >
                                {data.item}
                              </TruckSpecificationText>,
                            ],
                          )
                        ) : (
                          <></>
                        )}
                      </TruckSpecificationWrapper>
                      <ButtonWrapper>
                        {truckIsLoading?.id === item.id ? (
                          <SpinnerWrapper>
                            <TdsSpinner
                              size="sm"
                              data-test-element-type={
                                TestElementTypeId.GarageViewGarageTabLoadTruckSpinner
                              }
                            />
                          </SpinnerWrapper>
                        ) : (
                          <TdsButton
                            variant="primary"
                            size="sm"
                            text={t2(TextId.LABEL_LOAD_TRUCK)}
                            data-test-element-type={
                              TestElementTypeId.GarageViewGarageTabLoadTruckButton
                            }
                            onClick={() => {
                              handleLoadTruck(item)
                            }}
                          />
                        )}
                        <TdsButton
                          variant="secondary"
                          size="sm"
                          text={t2(TextId.LABEL_DELETE_TRUCK)}
                          onClick={() => {
                            handleDeleteTruck(item)
                          }}
                        />
                      </ButtonWrapper>
                    </TdsAccordionItem>
                  </TdsAccordion>
                </TruckCard>
              ))}
            </ContentContainer>
          )}
        </TabContentContainer>
      )}

      {tabContent === SidePanelGarageAccountContent.account && (
        <TabContentContainer
          data-test-element-type={
            TestElementTypeId.GarageViewAccountTabContainer
          }
        >
          <Header>{t2(TextId.HEADER_MANAGE_ACCOUNT)}</Header>
          <TextWrapper>{errorMessage}</TextWrapper>
          <TextWrapper>{t2(TextId.ACCOUNT_TEXT_LOGOUT)}</TextWrapper>
          <TdsButton
            variant="primary"
            size="md"
            text={t2(TextId.LABEL_ACTION_LOGOUT)}
            onClick={handleLogout}
            data-test-element-type={
              TestElementTypeId.GarageViewAccountViewSignOutButton
            }
          />
          <Divider></Divider>
          <TextWrapper>{t2(TextId.TEXT_MANAGE_ACCOUNT)}</TextWrapper>
          <TdsButton
            variant="secondary"
            size="md"
            text={t2(TextId.ACTION_LABEL_DELETE_ACCOUNT)}
            onClick={handleDeleteAccountClick}
            data-test-element-type={
              TestElementTypeId.GarageViewAccountViewGotoDeleteAccountFormButton
            }
          />
        </TabContentContainer>
      )}

      {tabContent === SidePanelGarageAccountContent.deleteAccount && (
        <TabContentContainer
          data-test-element-type={
            TestElementTypeId.GarageViewAccountTabContainer
          }
        >
          <Header>{t2(TextId.HEADER_MANAGE_ACCOUNT)}</Header>
          <TextWrapper>{errorMessage}</TextWrapper>
          <ConfirmViewText>{t2(TextId.ACCOUNT_DELETE_MESSAGE)}</ConfirmViewText>
          <ButtonContainer>
            <TdsButton
              variant="primary"
              size="md"
              text={'Remove'}
              onClick={handleDeleteAccount}
              data-test-element-type={
                TestElementTypeId.GarageViewAccountViewDeleteAccountSubmitButton
              }
            />
            <TdsButton
              variant="secondary"
              size="md"
              onClick={() =>
                setTabContent(SidePanelGarageAccountContent.account)
              }
              text={t2(TextId.LABEL_ACTION_CANCEL)}
            />
          </ButtonContainer>
        </TabContentContainer>
      )}
    </GarageRoot>
  )
}
